// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useServices } from '@/app-shell/services'
import type { NodeAdminViewProps } from '../../../../core/types'
import type {
  KnowledgeBaseAdminResponse,
  KnowledgeBaseItem,
  KnowledgeBaseMode,
  KnowledgeBaseSettingKey,
} from '../types'
import { knowledgeBaseQueryKey } from '../core/queryKeys'
import { readSettingsFromConfig, resolveSettingValue } from '../utils/settings'
import type { RawKnowledgeBaseSettings } from '../utils/settings'

const LOG_SCOPE = '[knowledge-base:admin]'

type UseAdminKnowledgeBaseArgs = NodeAdminViewProps

interface AdminSettingsState {
  mode: KnowledgeBaseMode
  injectionTemplate: string
  itemSchemaId: string | null
  scoreLabel: string
  scoreExplanation: string
  updateSetting: (key: KnowledgeBaseSettingKey, value: string | null) => void
}

// Findings mode uses hardcoded "Risk" terminology
const SCORE_LABEL = 'Risk'
const SCORE_EXPLANATION = 'The risk level (1 = low, 5 = critical).'

interface UseAdminKnowledgeBaseResult {
  loading: boolean
  error: string | null
  items: KnowledgeBaseItem[]
  itemCount: number
  updatedAt: string | null
  updatedBy: string | null
  formattedUpdated: string | null
  refresh: () => Promise<void>
  saveSettings: () => Promise<boolean>
  settings: AdminSettingsState
  label: string
  setLabel: (value: string) => void
  description: string
  setDescription: (value: string) => void
}

export function useAdminKnowledgeBase({
  orgId = 'umd-org',
  workspaceId,
  config,
  nodeInstanceId,
  nodeDefinition: _nodeDefinition,
  onChange,
}: UseAdminKnowledgeBaseArgs): UseAdminKnowledgeBaseResult {
  const services = useServices()
  const client = services.getClient()
  const queryClient = useQueryClient()

  const resolvedInstanceId = nodeInstanceId

  const basePath = useMemo(() => {
    if (!orgId || !workspaceId || !resolvedInstanceId) {
      return null
    }
    return `/org/${orgId}/ws/${workspaceId}/knowledge_base/instances/${resolvedInstanceId}`
  }, [orgId, workspaceId, resolvedInstanceId])

  const queryKey = knowledgeBaseQueryKey(orgId, workspaceId, resolvedInstanceId)

  const [saveError, setSaveError] = useState<string | null>(null)
  const [settingsState, setSettingsState] = useState<RawKnowledgeBaseSettings>(() => readSettingsFromConfig(config))
  const [labelState, setLabelState] = useState<string>('')
  const [descriptionState, setDescriptionState] = useState<string>('')

  const {
    data: response,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await client.get<KnowledgeBaseAdminResponse | null>(`${basePath}/knowledge-base`)
      return data ?? null
    },
    enabled: !!basePath,
    staleTime: 5 * 60_000,
  })

  const items: KnowledgeBaseItem[] = response?.items ?? []
  const itemCount = response?.itemCount ?? items.length
  const updatedAt = response?.updatedAt ?? null
  const updatedBy = response?.updatedBy ?? null

  const loading = queryLoading
  const loadError = !basePath
    ? 'Knowledge base widget is missing a node instance identifier. Update layout instanceId.'
    : queryError
      ? (queryError instanceof Error ? queryError.message : 'Unable to load knowledge base')
      : null
  const error = saveError ?? loadError

  // Sync settings from server response into local state when query data arrives.
  useEffect(() => {
    if (response?.settings) {
      setSettingsState(response.settings as RawKnowledgeBaseSettings)
    }
    if (response?.label !== undefined) {
      setLabelState(response.label ?? '')
    }
    if (response?.description !== undefined) {
      setDescriptionState(response.description ?? '')
    }
  }, [response])

  useEffect(() => {
    if (!basePath) {
      console.warn(`${LOG_SCOPE} skipping load`, {
        orgId,
        workspaceId,
        instanceId: resolvedInstanceId,
        reason: 'missing-base-path',
      })
    }
  }, [basePath, orgId, workspaceId, resolvedInstanceId])

  useEffect(() => {
    setSettingsState(prev => {
      if (Object.keys(prev).length > 0) {
        return prev
      }
      return readSettingsFromConfig(config)
    })
    // When config changes externally (e.g., generic ConfigurationPanel save path),
    // the cached KB response is stale and would clobber state via the response-sync effect above.
    queryClient.invalidateQueries({ queryKey })
  }, [config, queryClient, queryKey])

  const loadKnowledgeBase = useCallback(async () => {
    await refetch()
  }, [refetch])

  const updateSetting = useCallback(
    (key: KnowledgeBaseSettingKey, value: string | null) => {
      const sanitized = value && value.trim().length > 0 ? value.trim() : null
      const next = { ...settingsState }
      if (sanitized === null) {
        delete next[key]
      } else {
        next[key] = sanitized
      }

      setSettingsState(next)
      onChange?.('settings', next)
    },
    [onChange, settingsState],
  )

  const formattedUpdated = useMemo(() => {
    if (!updatedAt) {
      return null
    }
    const date = new Date(updatedAt)
    if (Number.isNaN(date.getTime())) {
      return updatedAt
    }
    return date.toLocaleString()
  }, [updatedAt])

  const modeValue = useMemo(() => {
    const raw = resolveSettingValue(settingsState, 'mode')
    return raw === 'report' ? 'report' : raw === 'criteria' ? 'criteria' : 'findings'
  }, [settingsState]) satisfies KnowledgeBaseMode

  const itemSchemaIdValue = useMemo(() => {
    const raw = settingsState.itemSchemaId
    return typeof raw === 'string' && raw.length > 0 ? raw : null
  }, [settingsState])

  const settings: AdminSettingsState = useMemo(() => ({
    mode: modeValue,
    injectionTemplate: resolveSettingValue(settingsState, 'injectionTemplate'),
    itemSchemaId: itemSchemaIdValue,
    scoreLabel: SCORE_LABEL,
    scoreExplanation: SCORE_EXPLANATION,
    updateSetting,
  }), [modeValue, itemSchemaIdValue, settingsState, updateSetting])

  const saveSettings = useCallback(async (): Promise<boolean> => {
    if (!basePath) return false
    try {
      await client.put(`${basePath}/settings`, {
        mode: modeValue,
        injectionTemplate: resolveSettingValue(settingsState, 'injectionTemplate') || null,
        itemSchemaId: itemSchemaIdValue,
        label: labelState.trim() || null,
        description: descriptionState.trim() || null,
      })
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save settings'
      console.error(`${LOG_SCOPE} save failure`, message)
      setSaveError(message)
      return false
    }
  }, [basePath, client, modeValue, itemSchemaIdValue, settingsState, labelState, descriptionState])

  return {
    loading,
    error,
    items,
    itemCount,
    updatedAt,
    updatedBy,
    formattedUpdated,
    refresh: loadKnowledgeBase,
    saveSettings,
    settings,
    label: labelState,
    setLabel: setLabelState,
    description: descriptionState,
    setDescription: setDescriptionState,
  }
}
